import { useEffect, useState, useContext } from "react";
import { Grid, InputAdornment, IconButton, Paper, TextField, Button, TablePagination } from "@mui/material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import styles from "./styles.module.css";
import { ROUTES } from "const";
import VendorContext from "context/VendorContext";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import DataTable from "components/utils/DataTable";
import Info from "components/utils/Alert/Info";
import AddVendor from "components/popups/AddVendor";
import { ReactComponent as VendorIcon } from "images/svg/truck.svg";
import SearchIcon from "images/svg/search.svg";
import ShopContext from "context/ShopContext";
import validityExpired from "components/utils/validityExpired";
import RestrictionInfo from "components/utils/Alert/RestrictionInfo";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";

function VendorsPage() {
  const { selectedShop } = useContext(ShopContext);
  const { vendors: contextVendors, loading, addVendor: contextAddVendor } = useContext(VendorContext);

  const [vendors, setVendors] = useState([]);
  const [addVendorDialog, setAddVendorDialog] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const navigate = useNavigate();

  const headerData = [
    {
      label: "Code",
      id: "code",
      type: "text",
    },
    {
      label: "Name",
      id: "name",
      type: "text",
    },
    {
      label: "Mobile",
      id: "mobile",
      type: "text",
    },
    {
      label: "Place",
      id: "address",
      type: "text",
    },
    {
      label: "Balance",
      id: "balance",
      type: "callback",
      viewRender: (row) => {
        const balance = Number(row?.balance) || 0;
        const balanceLabel = NumberFormatHelper.customerAmountFormat(balance);
        const balanceStyle = balance >= 0 ? { color: "green" } : { color: "red" };
        return <span style={balanceStyle}>{balanceLabel}</span>;
      },
    },
  ];

  const handleVendorDetails = (vendor) => {
    navigate(ROUTES.VENDOR_DETAILS.replace(":vendorId", vendor.id));
  };

  const handleAddVendorDialog = () => {
    setAddVendorDialog(true);
  };

  const handleAddVendor = async (vendor) => {
    try {
      await contextAddVendor(vendor);
      setAddVendorDialog(false);
      toast.success("Vendor created successfully");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSearch = (value) => {
    setFilterText(value);
  };

  useEffect(() => {
    if (!contextVendors) return;

    const filtered = contextVendors.filter(
      (v) =>
        (v.name || "").toLowerCase().includes(filterText.toLowerCase()) ||
        (v.mobile || "").toLowerCase().includes(filterText.toLowerCase()) ||
        (v.location || "").toLowerCase().includes(filterText.toLowerCase()),
    );

    setVendors(filtered);
    setPage(0);
  }, [contextVendors, filterText]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedVendors = vendors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Grid style={{ padding: "10px", height: "100%" }}>
      <Grid container className={styles.container} rowGap={1.25}>
        <Grid item xs={12} className={styles.headerContainer}>
          <Grid className={styles.header}>
            <h2>Vendors</h2>
            <Grid>
              <h5>Manage and Track Vendor Details</h5>
            </Grid>
          </Grid>

          <Grid item xs={12} className={styles.contentWrapper}>
            <TextField
              placeholder="Search"
              size="small"
              className={styles.searchField}
              value={filterText}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" style={{ padding: "0" }}>
                      <img src={SearchIcon} alt="Search" className={styles.icon} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <div className={styles.bar}> </div>
            <Button
              onClick={handleAddVendorDialog}
              className={styles.addCustBtn}
              disabled={selectedShop && validityExpired(selectedShop.customer_support_validity)}
            >
              <VendorIcon className={styles.icon} />
              Add New Vendor
            </Button>
          </Grid>
        </Grid>
        {selectedShop && validityExpired(selectedShop.customer_support_validity) ? (
          <Grid item xs={12} style={{ padding: "0 10px" }}>
            <RestrictionInfo
              title={"Vendor Support Feature not available "}
              content={"To get Vendor support options, contact support team."}
            />
          </Grid>
        ) : (
          <Grid item xs={12} className={styles.tableContainer}>
            {loading ? (
              <SkeletonLoading isOpen={true} count={40} height={40} xs={12} lg={1.5} />
            ) : vendors && vendors.length ? (
              <Paper
                elevation={0}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <div style={{ flex: 1, overflowY: "auto" }}>
                  <DataTable
                    columns={headerData}
                    rows={paginatedVendors}
                    rowClickListener={(row) => handleVendorDetails(row)}
                  />
                </div>
                <div className={styles.pagination}>
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 50]}
                    component="div"
                    count={vendors.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </div>
              </Paper>
            ) : (
              <Info
                title="No vendors to list"
                content="You have no vendors to list with current filter configuration. Please clear the filters or create a vendor"
              />
            )}
          </Grid>
        )}
      </Grid>
      {addVendorDialog && (
        <AddVendor closeDialog={() => setAddVendorDialog(false)} addVendorHandler={handleAddVendor} />
      )}
    </Grid>
  );
}

export default VendorsPage;
